import styled from '@emotion/styled';
import { Icon } from '@mdi/react';
import { Link } from 'react-router-dom';
import { Variables } from '../base/variables/variables';

export const SearchBarContainer = styled.div`
    position: relative;
    width: 100%;
`;

export const SearchInputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;

    /* Style the BaseInput component inside */
    & > div {
        width: 100%;
    }

    /* Style the input inside BaseInput */
    input {
        height: 32px;
        padding-left: 29px; /* 7px icon position + 18px icon width + 4px gap */
        border: none;
        border-radius: 32px;
        font-size: 14px;
        background-color: ${Variables.Swatches.Gray3};

        &::placeholder {
            color: ${Variables.Swatches.Gray5};
            font-size: 14px;
        }

        &:hover {
            background-color: ${Variables.Swatches.Gray3};
            border-color: transparent;
        }

        &:focus,
        &:active {
            background-color: ${Variables.Colors.white};
            border: 1px solid ${Variables.Colors.b500};
            box-shadow: 0 0 0 3px ${Variables.Swatches.AlphaMiddleBlue20};
        }
    }

    /* Style the inner postfix button (filter button) from BaseInput */
    button[type="button"] {
        background: transparent;
        border-radius: ${Variables.Rounded.large};
        width: 24px;
        height: 24px;
        margin-left: -30px;

        svg {
            width: 16px;
            height: 16px;
            fill: ${Variables.Swatches.Blue11};
        }

        &:hover {
            background-color: ${Variables.Swatches.AlphaMiddleGray40};

            svg {
                fill: ${Variables.Swatches.Blue11};
            }
        }

        &:active {
            background-color: ${Variables.Swatches.AlphaMiddleGray60};
            box-shadow: none;
        }
    }

    /* When popover is open, show background on the button */
    &[data-popover-open="true"] button[type="button"] {
        background: ${Variables.Swatches.AlphaMiddleGray40};

        &:hover {
            background-color: ${Variables.Swatches.AlphaMiddleGray60};
        }

        &:active {
            background-color: ${Variables.Swatches.AlphaMiddleGray80};
        }
    }
`;

export const SearchIcon = styled(Icon)`
    position: absolute;
    left: 7px;
    width: 18px;
    height: 18px;
    fill: ${Variables.Swatches.Gray7};
    pointer-events: none;
    z-index: 1;
`;

export const PopoverContent = styled.div`
    background: ${Variables.Swatches.Gray1};
    border: 1px solid ${Variables.Swatches.Gray3};
    border-radius: ${Variables.Rounded.large};
    box-shadow: 0px 8px 16px 0px ${Variables.Swatches.AlphaDarkGray10};
    width: 575px;
    max-width: 90vw;
    padding: ${Variables.Rounded.large};
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;
`;

export const PopoverHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const PopoverTitle = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: ${Variables.FontWeight.semibold};
    font-family: ${Variables.Fonts.calibre};
    color: ${Variables.Swatches.Gray8};
    line-height: normal;
`;

export const CloseButton = styled.button`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: ${Variables.transition};
    padding: 4px;

    svg {
        width: 16px;
        height: 16px;
        fill: ${Variables.Swatches.Gray7};
    }

    &:hover {
        background-color: ${Variables.Swatches.AlphaMiddleGray40};
        
        svg {
            fill: ${Variables.Swatches.Gray8};
        }
    }

    &:active {
        background-color: ${Variables.Swatches.AlphaMiddleGray60};
    }
`;

export const PopoverBody = styled.div`
    border-top: 1px solid ${Variables.Swatches.Gray3};
    padding-top: 8px;
`;

export const FormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
`;

export const FormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;

    /* Style BaseInput components inside form fields */
    input {
        height: 32px;
        border-radius: 4px;
        font-size: 14px;
    }
`;

export const FormFieldFullWidth = styled(FormField)`
    grid-column: 1 / -1;
`;

export const Label = styled.label`
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    font-family: ${Variables.Fonts.calibre};
    color: ${Variables.Swatches.Gray8};
    line-height: normal;
`;

export const PopoverFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
`;

export const Divider = styled.div`
    height: 1px;
    background-color: ${Variables.Swatches.Gray3};
    margin-left: -${Variables.Rounded.large};
    margin-right: -${Variables.Rounded.large};
    margin-top: ${Variables.Rounded.large};
    margin-bottom: ${Variables.Rounded.large};
`;

export const SearchResultsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const SearchResultsHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 24px;
`;

export const SearchResultsContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    max-height: 400px;
`;

export const SearchResultsTitle = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: ${Variables.FontWeight.semibold};
    font-family: ${Variables.Fonts.calibre};
    color: ${Variables.Swatches.Gray8};
    line-height: normal;
`;

export const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

export const ToggleLabel = styled.span`
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    font-family: ${Variables.Fonts.calibre};
    color: ${Variables.Swatches.Gray8};
    line-height: normal;
`;

export const PatientCard = styled.div`
    background: ${Variables.Swatches.Gray1};
    border-top: 1px solid ${Variables.Swatches.Gray3};
    display: flex;
    flex-direction: column;
    gap: ${Variables.Rounded.large};
    padding-top: 8px;
    position: relative;
    z-index: 2;
`;

export const PatientHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const PatientInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    width: 209px;
`;

export const PatientIcon = styled.div`
    display: flex;
    align-items: center;
    overflow: hidden;
    width: 20px;
    height: 20px;
    flex-shrink: 0;

    svg {
        width: 20px;
        height: 20px;
        fill: ${Variables.Swatches.Gray7};
    }
`;

export const PatientNameContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    line-height: normal;
`;

export const PatientLabel = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: ${Variables.FontWeight.medium};
    font-family: ${Variables.Fonts.calibre};
    color: ${Variables.Swatches.Gray8};
`;

export const PatientName = styled.p`
    margin: 0;
    font-size: 16px;
    font-weight: ${Variables.FontWeight.semibold};
    font-family: ${Variables.Fonts.calibre};
    color: ${Variables.Swatches.Gray9};
`;

export const PatientLink = styled(Link)`
    margin: 0;
    font-size: 16px;
    font-weight: ${Variables.FontWeight.semibold};
    font-family: ${Variables.Fonts.calibre};
    color: ${Variables.Swatches.Gray9};
    text-decoration: none;
    line-height: normal;

    &:hover {
        color: ${Variables.Swatches.Blue11};
        text-decoration: underline;
    }
`;

export const PatientMetadata = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
`;

export const PatientDetails = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    line-height: normal;
    padding: 2px 0;
    color: ${Variables.Swatches.Gray8};
`;

export const DetailRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const DetailLabel = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: ${Variables.FontWeight.medium};
    font-family: ${Variables.Fonts.calibre};
`;

export const DetailValue = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: ${Variables.FontWeight.regular};
    font-family: ${Variables.Fonts.calibre};
`;

export const InfoIconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 4px;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    
    svg {
        width: 16px;
        height: 16px;
        fill: ${Variables.Swatches.Gray8};
    }

    &:hover {
        background-color: ${Variables.Swatches.AlphaMiddleGray40};
    }

    &:active {
        background-color: ${Variables.Swatches.AlphaMiddleGray60};
    }
`;

export const ClaimsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    position: relative;
    z-index: 1;
`;

export const ClaimsHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const ClaimHeaderIcon = styled.div`
    width: 20px;
    height: 20px;
    flex-shrink: 0;
`;

export const ClaimHeaderColumns = styled.div`
    display: flex;
    flex: 1;
    gap: 16px;
`;

export const ClaimHeaderColumn = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    p {
        margin: 0;
        font-size: 12px;
        font-weight: ${Variables.FontWeight.medium};
        font-family: ${Variables.Fonts.calibre};
        color: ${Variables.Swatches.Gray8};
        line-height: normal;
    }
`;

export const ClaimRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const ClaimIcon = styled.div`
    display: flex;
    align-items: center;
    overflow: hidden;
    width: 18px;
    height: 18px;
    flex-shrink: 0;

    svg {
        width: 18px;
        height: 18px;
        fill: ${Variables.Swatches.Gray7};
    }
`;

export const ClaimColumns = styled.div`
    display: flex;
    flex: 1;
    gap: 16px;
`;

export const ClaimColumn = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
`;

export const ClaimLink = styled(Link)`
    margin: 0;
    font-size: 14px;
    font-weight: ${Variables.FontWeight.regular};
    font-family: ${Variables.Fonts.calibre};
    color: ${Variables.Swatches.Gray8};
    text-decoration: none;
    line-height: normal;

    &:hover {
        color: ${Variables.Swatches.Blue11};
        text-decoration: underline;
    }
`;

export const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    color: ${Variables.Swatches.Gray7};
    font-size: 14px;
    font-family: ${Variables.Fonts.calibre};
`;

export const ErrorContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    color: ${Variables.Swatches.Red5};
    font-size: 14px;
    font-family: ${Variables.Fonts.calibre};
`;

export const EmptyContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    color: ${Variables.Swatches.Gray7};
    font-size: 14px;
    font-family: ${Variables.Fonts.calibre};
`;

export const ClearButton = styled.button`
    min-width: 80px;
    height: 32px;
    padding: 0 16px;
    border-radius: 4px;
    border: 1px solid ${Variables.Swatches.Gray4};
    background-color: ${Variables.Colors.white};
    color: ${Variables.Swatches.Gray8};
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    font-family: ${Variables.Fonts.calibre};
    cursor: pointer;
    transition: ${Variables.transition};

    &:hover {
        background-color: ${Variables.Swatches.Gray2};
    }

    &:active {
        background-color: ${Variables.Swatches.Gray3};
    }
`;

export const SubmitButton = styled.button<{ disabled?: boolean }>`
    min-width: 80px;
    height: 32px;
    padding: 0 16px;
    border-radius: 4px;
    border: none;
    background-color: ${props => props.disabled ? Variables.Swatches.Gray4 : Variables.Swatches.Blue5};
    color: ${Variables.Colors.white};
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    font-family: ${Variables.Fonts.calibre};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: ${Variables.transition};

    &:hover {
        background-color: ${props => props.disabled ? Variables.Swatches.Gray4 : Variables.Colors.b500};
    }

    &:active {
        background-color: ${props => props.disabled ? Variables.Swatches.Gray4 : Variables.Colors.b500};
    }
`;
